import { useOrganization } from 'context/OrganizationContext';
import { Horse, ModuleEnum, SexEnum } from 'openapi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiPromises } from 'utilities/ApiPromises';
import ApiErrorParser, { ApiErrorType } from 'api/ApiErrorParser';
import { useTranslation } from 'react-i18next';
import { predefinedApiRequestActiveOrganizationHorses, predefinedApiRequestProductHorses } from 'utilities/ApiRequests';
import useModuleAccess from 'hooks/UseModuleAccess';
import { WrappedComboboxProps } from 'ui/Inputs/SelectList';

interface ReturnType {
  stallionOptions: WrappedComboboxProps<Horse>[] | Horse[];
  stallionProductWarning: string | undefined;
}

interface Props {
  modalIsOpen: boolean;
  horses: Horse[] | undefined;
  stallionUidFormValue: string | undefined;
  addInseminationDateFormValue: string | undefined;
}

/**
 * Small hook to handle the breeding plan selection for the semen order
 */
function useMareCycleStallion({ modalIsOpen, horses: givenHorses, stallionUidFormValue, addInseminationDateFormValue }: Props): ReturnType {
  const [stallionProducts, setStallionProducts] = useState<Horse[]>();
  const [horses, setHorses] = useState<Horse[] | undefined>(givenHorses);

  const { hasModuleAccess } = useModuleAccess();
  const { selectedOrganizationUid, generateCacheKey } = useOrganization();
  const { t } = useTranslation();

  const stallions = useMemo(() => {
    if (!horses) return [];
    return horses.filter(horse => horse.sex === SexEnum._1);
  }, [horses]);

  /* Load the Breeding Plans
   */
  const loadApiData = useCallback(() => {
    const promises = new ApiPromises();
    if (!selectedOrganizationUid) return promises;

    if (!horses) {
      promises.appendListObj<Horse>(
        'horses',
        setHorses,
        predefinedApiRequestActiveOrganizationHorses(selectedOrganizationUid, generateCacheKey),
      );
    }

    if (!stallionProducts) {
      promises.appendListObj<Horse>(
        'product-horses',
        setStallionProducts,
        predefinedApiRequestProductHorses(selectedOrganizationUid, generateCacheKey),
      );
    }

    return promises;
  }, [generateCacheKey, horses, selectedOrganizationUid, stallionProducts]);

  /**
   * Generate an error message in case the the user has the module Shop and Breeding,
   * the stallion is not orderable and the user has select a insemination date.
   */
  const stallionProductWarning = useMemo(() => {
    if (
      hasModuleAccess(ModuleEnum.SHOP_AND_BREEDING) &&
      addInseminationDateFormValue &&
      stallionProducts &&
      stallionUidFormValue &&
      !stallionProducts.some(horse => horse.uid === stallionUidFormValue)
    ) {
      return t('please-note-selected-stallion-not-orderable', 'Please note: selected stallion is not orderable. No order will be made.');
    }
  }, [addInseminationDateFormValue, hasModuleAccess, stallionProducts, stallionUidFormValue, t]);

  /**
   * Build the list of stallions to display in the selectInput component.
   *
   * We have two cases:
   * 1. The user has the module Shop and Breeding, in this case we show the stallion products first and then the other stallions.
   * 2. The user does not have the module Shop and Breeding, in this case we show all the stallions.
   */
  const stallionOptions = useMemo(() => {
    if (hasModuleAccess(ModuleEnum.SHOP_AND_BREEDING)) {
      const options: WrappedComboboxProps<Horse>[] = [
        {
          heading: t('stallion-products', 'Stallion products'),
          items: stallionProducts ?? [],
        },
      ];

      const otherStallions = (horses ?? []).filter(horse => !stallionProducts?.some(stallion => stallion.uid === horse.uid));
      if (otherStallions.length > 0) {
        options.push({
          heading: t('other-stallions', 'Other Stallions'),
          items: otherStallions,
        });
      }
      return options;
    }

    return stallions;
  }, [hasModuleAccess, horses, stallionProducts, stallions, t]);

  /**
   * Load the Api data
   */
  useEffect(() => {
    if (modalIsOpen) {
      const promise = loadApiData();
      promise.watchAll().catch(e => {
        const error = new ApiErrorParser(e);
        if (error.errorType !== ApiErrorType.CancelError) {
          console.warn('Failed to fetch ApiData for the mareCycleStallion.', e);
        }
      });

      return () => promise.cancel();
    }
  }, [loadApiData, modalIsOpen]);

  return {
    stallionOptions,
    stallionProductWarning,
  };
}

export default useMareCycleStallion;
